/**
 * HelpTooltip Component
 * 
 * Provides inline help tooltips for admin portal UI elements.
 * Shows a help icon that displays contextual help on hover/click.
 */

import React, { useState, useRef, useEffect } from 'react';

export const HELP_CONTENT = {
  dashboard: {
    overview: 'The dashboard provides a quick overview of key metrics and system health.',
    totalUsers: 'Total number of registered users in the system.',
    activeSubscriptions: 'Number of currently active paid subscriptions.',
    monthlyRevenue: 'Total revenue collected in the current month.',
    systemHealth: 'Overall health status of backend services.',
  },
  
  users: {
    search: 'Search users by email, name, phone number, or user ID.',
    status: 'User account status: CONFIRMED (verified), UNCONFIRMED (pending verification).',
    disable: 'Disabling a user prevents them from logging in but preserves their data.',
    resetPassword: 'Sends a password reset email to the user.',
    groups: 'Cognito groups determine user permissions.',
  },
  
  subscriptions: {
    status: 'Subscription status: active, cancelled, expired, or trial.',
    mrr: 'Monthly Recurring Revenue - total expected monthly revenue.',
    extend: 'Add days to a subscription for customer service issues.',
    cancel: 'Cancel subscription immediately or at end of billing period.',
  },
  
  roles: {
    admin: 'Standard admin access. Can view most data.',
    superAdmin: 'Full access including user management and audit logs.',
  },
};

const HelpTooltip = ({ 
  content, 
  position = 'top', 
  size = 'small',
  children 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      let top = 0;
      let left = 0;
      
      switch (position) {
        case 'top':
          top = -tooltipRect.height - 8;
          left = (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'bottom':
          top = triggerRect.height + 8;
          left = (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'left':
          top = (triggerRect.height - tooltipRect.height) / 2;
          left = -tooltipRect.width - 8;
          break;
        case 'right':
          top = (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.width + 8;
          break;
        default:
          break;
      }
      
      setTooltipPosition({ top, left });
    }
  }, [isVisible, position]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);
  const handleClick = () => setIsVisible(!isVisible);

  const sizeClass = `admin-help-tooltip__icon--${size}`;

  return (
    <span 
      className="admin-help-tooltip-container"
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children || (
        <button 
          className={`admin-help-tooltip__icon ${sizeClass}`}
          onClick={handleClick}
          aria-label="Help"
          type="button"
        >
          ?
        </button>
      )}
      {isVisible && content && (
        <div 
          className={`admin-help-tooltip admin-help-tooltip--${position}`}
          ref={tooltipRef}
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
          role="tooltip"
        >
          <div className="admin-help-tooltip__content">
            {content}
          </div>
          <div className={`admin-help-tooltip__arrow admin-help-tooltip__arrow--${position}`} />
        </div>
      )}
    </span>
  );
};

export default HelpTooltip;
